import { apiRequest } from './api.js';
import API_CONFIG from './api.js';

// Chat and Query related API calls
export const chatService = {  // Send a query to the backend and get a response
  sendQuery: async ({ query, agentType, sessionId, sessionToken, token, excel_data, pivot_enabled, signal }) => {
    const url = `${API_CONFIG.BACKEND_URL}/query`;
    
    const requestBody = {
      query,
      agent_type: agentType,
      session_id: sessionId
    };
    
    // Add excel_data if provided
    if (excel_data) {
      requestBody.excel_data = excel_data;
    }
    
    // Add isPivot if provided
    if (pivot_enabled) {
      requestBody.pivot_enabled = true;
    }
    
    try {
      return await apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        token,
        signal, // Pass the abort signal
        headers: {
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });    } catch (error) {
      // Handle timeout errors specifically
      if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        console.warn('Query request timed out after 90 seconds');
        throw new Error('Query timeout exceeded (90 seconds). Please check your connection and try again.');
      }
      
      // If the error has a response body (from 500 errors), return it as the response
      // This preserves session_id, question_id, and other useful data from the backend
      if (error.response) {
        return {
          ...error.response,
          error: true,
          textual_response: error.message
        };
      }
      
      // For other errors (network, abort, etc.), re-throw
      throw error;
    }
  },
  // Send user feedback for a specific message
  sendFeedback: async ({ sessionId, questionId, isLiked, feedbackDescription = "", token }) => {
    const url = `${API_CONFIG.BACKEND_URL}/feedback`;
    
    const requestBody = {
      session_id: sessionId,
      question_id: questionId,
      feedback: isLiked,
      feedback_description: feedbackDescription
    };
    
    return await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      token,
      headers: {
        'Accept-Language': 'en-US,en;q=0.9'
      }
    });
  },  // Download Excel file for a specific query result
  downloadExcel: async ({ sessionId, questionId, token }) => {
    // Validate inputs
    if (!sessionId || !questionId) {
      return {
        success: false,
        error: 'Session ID and Question ID are required'
      };
    }

    if (!token) {
      return {
        success: false,
        error: 'Authentication token is required'
      };
    }

    // Use the localhost URL as specified in the requirements
    const url = `${API_CONFIG.BACKEND_URL}/download-excel?session_id=${sessionId}&question_id=${questionId}`;
    
    try {
      console.log('Calling download Excel API:', url);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `Server error (${response.status})`;
        
        try {
          // Try to get error details from response body
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorJson = JSON.parse(errorText);
              errorMessage = errorJson.message || errorJson.error || errorMessage;
            } catch {
              // If not JSON, use the text as error message
              errorMessage = errorText.length > 100 ? errorMessage : errorText;
            }
          }
        } catch (parseError) {
          console.warn('Could not parse error response:', parseError);
        }

        switch (response.status) {
          case 400:
            errorMessage = `Bad request: ${errorMessage}`;
            break;
          case 401:
            errorMessage = 'Authentication failed. Please sign in again.';
            break;
          case 403:
            errorMessage = 'Access denied. You do not have permission to download this file.';
            break;
          case 404:
            errorMessage = 'File not found. The requested data may no longer be available.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          case 503:
            errorMessage = 'Service temporarily unavailable. Please try again in a few moments.';
            break;
          default:
            errorMessage = `Server responded with ${response.status}: ${errorMessage}`;
        }

        throw new Error(errorMessage);
      }      // Validate response headers
      const contentType = response.headers.get('content-type') || '';
      const contentLength = response.headers.get('content-length');
      
      console.log('Download response headers:', {
        contentType,
        contentLength,
        status: response.status
      });

      // Check if content length is 0
      if (contentLength === '0') {
        throw new Error('Server returned an empty file');
      }

      // Check if response is JSON (new API format) or binary (old format)
      if (contentType.includes('application/json')) {
        console.log('API returned JSON response, parsing...');
        
        try {
          const jsonResponse = await response.json();
          
          // Validate JSON response structure
          if (!jsonResponse.status || jsonResponse.status !== 'success') {
            throw new Error(jsonResponse.message || 'API returned unsuccessful status');
          }
          
          if (!jsonResponse.data) {
            throw new Error('API response missing Excel data');
          }
          
          console.log('JSON response parsed successfully:', {
            status: jsonResponse.status,
            filename: jsonResponse.filename,
            dataLength: jsonResponse.data ? jsonResponse.data.length : 0
          });
          
          return {
            success: true,
            response: jsonResponse,
            contentType: jsonResponse.mime_type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            contentDisposition: null // Not applicable for JSON response
          };
          
        } catch (parseError) {
          console.error('Error parsing JSON response:', parseError);
          throw new Error('Failed to parse server response');
        }
        
      } else {
        // Handle binary/blob response (original format)
        console.log('API returned binary response');
        
        return {
          success: true,
          response: response,
          contentType: contentType,
          contentDisposition: response.headers.get('content-disposition')
        };
      }
      
    } catch (error) {
      console.error('Excel download API error:', error);
      
      let errorMessage = 'Failed to download Excel file';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Download request timed out. Please try again.';
      } else if (error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }
};

export default chatService;
